/* ──────────────────────────────────────────────────────────────────────────
 * Rates — branded types and named constructors for financial conventions.
 *
 * Different financial instruments quote rates using different conventions.
 * Mixing them silently produces wrong math (the Slice 2 portfolio over-
 * shoot was the lesson). This module makes the convention part of the
 * TYPE — TypeScript refuses to let you pass a stock return where a HELOC
 * rate is expected, or call the wrong conversion function on a rate.
 *
 * To add a new instrument: pick the right convention, add a named
 * constructor, done. See `RATES.md` in this directory for the canonical
 * table of conventions and the instruments that use each.
 * ────────────────────────────────────────────────────────────────────────── */

declare const RateBrand: unique symbol;

type Branded<B extends string> = number & { readonly [RateBrand]: B };

// ── Branded rate types — each represents one convention ───────────────────

/**
 * **CAGR** (compound annual growth rate, a.k.a. effective annual return).
 * The convention used by every ETF factsheet, mutual fund report, and
 * index return quote. "S&P 500 returned 10% per year" means 10% CAGR —
 * money compounds to exactly 10% over each year.
 */
export type Cagr = Branded<"cagr">;

/**
 * **Nominal annual rate, compounded monthly.** The convention used by
 * Canadian HELOCs, PLOCs, brokerage margin loans, investment loans, and
 * credit cards. "6.0% HELOC" means the bank charges 0.5% per month
 * (6.0/12), which compounds to 6.168% effective annual.
 */
export type NominalMonthly = Branded<"nominal_monthly">;

/**
 * **Nominal annual rate, compounded semi-annually.** The convention
 * mandated for Canadian residential mortgages by Section 6 of the
 * Interest Act. A "5% mortgage" compounds twice a year, not monthly.
 */
export type NominalSemiAnnual = Branded<"nominal_semiannual">;

// ── Named constructors — every instrument has its own ─────────────────────
//
// The constructor's NAME documents the convention. When you read
// `helocRate(0.06)` in code, you know the convention is nominal-monthly
// without looking it up. Adding a new instrument = one new line here.

/** Stock or ETF return, quoted as CAGR. e.g., `stockReturn(0.10)` = 10%/yr CAGR. */
export const stockReturn = (annual: number): Cagr => annual as Cagr;

/** ETF return, quoted as CAGR. Alias of stockReturn — kept for readability. */
export const etfReturn = (annual: number): Cagr => annual as Cagr;

/** Index return (e.g., TSX, S&P 500), quoted as CAGR. */
export const indexReturn = (annual: number): Cagr => annual as Cagr;

/** Canadian HELOC rate. Nominal annual, compounded monthly. */
export const helocRate = (annual: number): NominalMonthly => annual as NominalMonthly;

/** Canadian PLOC (personal line of credit) rate. Nominal annual, compounded monthly. */
export const plocRate = (annual: number): NominalMonthly => annual as NominalMonthly;

/** Brokerage margin loan rate. Nominal annual, compounded monthly. */
export const marginRate = (annual: number): NominalMonthly => annual as NominalMonthly;

/** Investment loan rate (B2B Bank, Manulife, etc.). Nominal annual, compounded monthly. */
export const investmentLoanRate = (annual: number): NominalMonthly => annual as NominalMonthly;

/** Credit card rate. Nominal annual, compounded monthly (close approximation of daily). */
export const creditCardRate = (annual: number): NominalMonthly => annual as NominalMonthly;

/** Canadian residential mortgage rate. Nominal annual, compounded semi-annually per Interest Act §6. */
export const mortgageRate = (annual: number): NominalSemiAnnual => annual as NominalSemiAnnual;

// ── Monthly conversion — each convention has its own formula ──────────────

/**
 * Per-month growth factor for a CAGR rate.
 *
 * After 12 months, `value × monthlyFactorFromCagr(r)^12 = value × (1 + r)` exactly.
 * Used to advance a CAGR-stated portfolio one month forward.
 */
export const monthlyFactorFromCagr = (rate: Cagr): number => Math.pow(1 + rate, 1 / 12);

/**
 * Per-month interest rate for a nominal-annual rate compounded monthly.
 *
 * Example: `monthlyRateFromNominalMonthly(helocRate(0.06))` = `0.005`.
 */
export const monthlyRateFromNominalMonthly = (rate: NominalMonthly): number => rate / 12;

/**
 * Per-month interest rate for a nominal-annual rate compounded semi-annually.
 *
 * Derived from the semi-annual factor: `(1 + r/2)^(1/6) − 1`. Matches
 * Canadian mortgage convention (Interest Act §6).
 */
export const monthlyRateFromNominalSemiAnnual = (rate: NominalSemiAnnual): number =>
  Math.pow(1 + rate / 2, 1 / 6) - 1;
